/* @flow */

import type { Mode } from "state/view-mode";
import type { Customer } from "shop-state/types";

import React, { useContext } from "react";
import { Link, NavLink, useHistory } from "react-router-dom";
import { useTranslate } from "@awardit/react-use-translate";
import { Menu, CartMini } from "@crossroads/ui-components";
import { StoreInfoContext } from "entrypoint/shared";
import { useData } from "crustate/react";
import { QuoteData } from "data";
import cn from "classnames";
import SystemMessages from "components/SystemMessages";
import Logo from "components/AppHeader/Logo";
import CustomerBadge from "components/CustomerBadge";
import ExpiringPointsBanner from "components/ExpiringPointsBanner";
import { MODE } from "state/view-mode";
import { navRoutes } from "helpers/utils";
import { ROUTES, ADMIN_ROUTES } from "components/AppHeader/routes";
import SubMenu, { useHideSubMenu } from "components/AppHeader/Large/SubMenu";
import CartCounter from "components/AppHeader/CartCounter";
import HamburgerIcon from "icons/hamburger.svg";
import AdminButton from "components/AccountView/AdminButton";

import styles from "./styles.scss";

type Props = {
  className: string,
  mode: Mode,
  setMode: Mode => void,
  onHomeView: boolean,
  onCheckout: boolean,
  onSuccess: boolean,
  isScrolled: boolean,
  lastVisited: ?string,
  customer: ?Customer,
  logoWidth: string,
  adminUser?: boolean,
};

const noDimModes = [MODE.NORMAL, MODE.RECRUIT, MODE.TINK];

const AppHeaderLarge = ({
  className = "",
  mode,
  setMode,
  onHomeView,
  onCheckout,
  lastVisited,
  customer,
  logoWidth,
  isScrolled,
  onSuccess,
  adminUser,
}: Props): React$Node => {
  const t = useTranslate();
  const quoteData = useData(QuoteData);
  const { location: { pathname } } = useHistory();
  const hideSubMenu = useHideSubMenu();
  const storeInfo = useContext(StoreInfoContext);
  const {
    routes: {
      accountView,
      alternateContent1,
      alternateContent2,
      alternateContent3,
      homeView,
      suppliersView,
      checkoutView,
      checkoutSuccessView,
    },
    content: {
      accountview,
      appheader,
      tinkview,
    },
    info,
  } = storeInfo;
  const showAdminButton = Boolean(accountview.showAdministration) &&
    customer &&
    customer.awardit.userType !== null &&
    customer.awardit.userType !== undefined ?
    customer.awardit.userType > 0 :
    false;
  const compactMenu = appheader.compactMenu !== undefined &&
    appheader.compactMenu !== null &&
    appheader.compactMenu === true;
  const hasExtendedSubMenu = !hideSubMenu &&
    !(checkoutView?.url && pathname.includes(checkoutView.url)) &&
    !(checkoutSuccessView?.url && pathname.includes(checkoutSuccessView.url));

  let UPDATEDROUTES = null;

  if (tinkview.hideInNavbar !== undefined &&
    tinkview.hideInNavbar !== null &&
    tinkview.hideInNavbar === true) {
    UPDATEDROUTES = ROUTES.filter(item => item !== "accountTinkView");
  }

  const nav = navRoutes(
    UPDATEDROUTES ? UPDATEDROUTES : ROUTES,
    storeInfo,
    ADMIN_ROUTES,
    adminUser
  );

  return (
    <div className={styles.container}>
      <div className={cn(
        styles.block,
        className,
        { [styles.fullMenu]: onHomeView },
        { [styles.hasExtendedSubMenu]: hasExtendedSubMenu })}
      >
        {homeView && homeView.url &&
          <Link className={cn(styles.item, styles.logo)} to={homeView.url ?? "/"}>
            <Logo
              width={logoWidth}
              isInverted={!isScrolled}
              onClick={() => setMode(MODE.NORMAL)}
            />
          </Link>
        }

        <div className={styles.navContainer}>
          {accountView && accountView.url &&
            <ExpiringPointsBanner
              className={styles.expiringPointsBanner}
              customer={customer}
              to={`${accountView.url}/points`}
            />
          }

          <nav className={cn(styles.nav, "awardit-customerBadge")}>
            {showAdminButton &&
              <div className={cn(styles.adminBtnContainer, "awardit-adminButton")}>
                {accountview.administration &&
                  <AdminButton text={accountview.administration} />
                }
              </div>
            }
            {accountView && accountView.toggle !== undefined &&
            accountView.toggle && accountView.url && (
              <CustomerBadge
                accountUrl={accountView.url}
                customer={customer}
              />
            )}
            {!pathname.includes("/checkout/") && (
              <CartCounter
                className={cn(
                  styles.item,
                  styles.item__cart,
                  "awardit-appHeaderCartCounter",
                  { [styles.active]: mode === MODE.CART }
                )}
                isOpen={mode === MODE.CART}
                openMiniCart={() => setMode(MODE.CART)}
              >
                <p>{t("CART.TITLE")}</p>
              </CartCounter>
            )}
            {!pathname.includes("/checkout/") && mode === MODE.CART &&
              checkoutView && checkoutView.url && (
              <CartMini
                quoteData={quoteData}
                variant="b2b"
                altUrl={`${checkoutView.url}/cart`}
                ctaUrl={`${checkoutView.url}/shipping`}
                className={cn(styles.desktop, styles.miniCart)}
                onClose={() => setMode(MODE.NORMAL)}
              />
            )}
          </nav>
        </div>
      </div>

      {!onCheckout && (
        <nav className={cn(styles.nav, styles.linksNav, "awardit-appHeaderLinks")}>
          {(info.code === "releasefinans_sek" || info.code === "b2b_releasefinans_sek") &&
            <NavLink
              key="about-release"
              to="about-release"
              activeClassName={styles.active}
              className={styles.item}
            >
              Om Release Points
            </NavLink>
          }
          {(info.code === "postkodlotteriet_sek" || info.code === "b2b_postkodlotteriet_sek") &&
            <NavLink
              key="about-wownyckeln"
              to="about-wownyckeln"
              activeClassName={styles.active}
              className={styles.item}
            >
              Om Wownyckeln
            </NavLink>
          }

          {(info.code === "tele2_sek" || info.code === "b2b_tele2_sek") &&
            <div
              className={cn(styles.navDropdown, styles.item, "awardit-appHeaderLinksTele2")}
            >
              <span>Dokumentbibliotek</span>
              <section className={styles.navDropdownLinks}>

                {alternateContent1 &&
                alternateContent1.title &&
                alternateContent1.url &&
                  <NavLink
                    to={alternateContent1.url}
                    activeClassName={styles.active}
                    className={styles.item}
                  >
                    {alternateContent1.title}
                  </NavLink>
                }
                {alternateContent2 &&
                alternateContent2.title &&
                alternateContent2.url &&
                  <NavLink
                    to={alternateContent2.url}
                    activeClassName={styles.active}
                    className={styles.item}
                  >
                    {alternateContent2.title}
                  </NavLink>
                }
                {alternateContent3 &&
                alternateContent3.title &&
                alternateContent3.url &&
                  <NavLink
                    to={alternateContent3.url}
                    activeClassName={styles.active}
                    className={styles.item}
                  >
                    {alternateContent3.title}
                  </NavLink>
                }
              </section>
            </div>
          }
          {(info.code === "dreamteam_sek" || info.code === "b2b_dreamteam_sek") &&
            <NavLink
              key="about-dreamteam"
              to="about-dreamteam"
              activeClassName={styles.active}
              className={styles.item}
            >
              Tjäna poäng
            </NavLink>
          }

          {!compactMenu && nav.map(x => (x.url &&
            <NavLink
              key={x.url}
              to={x.url}
              activeClassName={styles.active}
              className={styles.item}
            >
              {x.title}
            </NavLink>
          ))}

          {!compactMenu && suppliersView &&
          suppliersView.toggle !== undefined && suppliersView.toggle &&
          suppliersView.url && (
            <NavLink
              key={suppliersView.url}
              to={suppliersView.url}
              activeClassName={styles.active}
              className={styles.item}
            >
              {suppliersView.title}
            </NavLink>
          )}

          {compactMenu &&
            <Menu className={styles.item} icon={<HamburgerIcon />}>
              {!onCheckout && nav.map(x => (x.url &&
                <Link key={x.url} to={x.url} className={styles.menuItem}>
                  {x.title}
                </Link>
              ))}
              {!onCheckout && suppliersView && suppliersView.toggle !== undefined &&
              suppliersView.toggle && suppliersView.url && (
                <Link to={suppliersView.url} className={styles.menuItem}>
                  {suppliersView.title}
                </Link>
              )}
            </Menu>
          }

        </nav>
      )}

      <SubMenu
        mode={mode}
        setMode={setMode}
        lastVisited={lastVisited}
        onCheckout={onCheckout}
        onSuccess={onSuccess} />

      {!noDimModes.includes(mode) &&
        <div className={styles.dim} onClick={() => setMode(MODE.NORMAL)} />
      }

      <div>
        <SystemMessages />
      </div>
    </div>
  );
};

export default AppHeaderLarge;
